import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSegments, toggleSegment, sendCommunication } from '../services/api';
import SqOffOrders from './SqOffOrders';
import ClientAlerts from './ClientAlerts';
import OrderReports from './OrderReports';
import DealerAccessPanel from './DealerAccessPanel';
import AuditLogs from './AuditLogs';
import UserManagement from './UserManagement';
import BFUpload from './BFUpload';
import ClientUpload from './ClientUpload';
import SystemLogsPanel from './SystemLogsPanel';
import SyncMonitor from './SyncMonitor';

/* ═══════════════════════════════════════════════════════════════════════════
   REAL-TIME ORDER NOTIFICATIONS — 3-second polling
   Polls /api/admin/notifications/pending every 3 seconds.
   New sq-off orders appear on admin screen within 3 seconds.
   Works reliably on Azure Windows App Service — no SSE/WebSocket needed.
   ═══════════════════════════════════════════════════════════════════════════*/

function playAlertSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [660, 880, 1100].forEach((freq, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.15);
      osc.start(ctx.currentTime + i * 0.18);
      osc.stop(ctx.currentTime + i * 0.18 + 0.15);
    });
  } catch (_) {}
}

function useOrderNotifications(onNewOrder) {
  const lastPollRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('nb_admin_token');
    if (!token) return;

    const authH = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

    const poll = async () => {
      try {
        const params = lastPollRef.current ? `?since=${encodeURIComponent(lastPollRef.current)}` : '';
        const res    = await fetch(`/api/admin/notifications/pending${params}`, { headers: authH });
        if (!res.ok) return;
        const data = await res.json();
        if (data.alerts && data.alerts.length > 0) {
          lastPollRef.current = new Date().toISOString();
          data.alerts.forEach(alert => onNewOrder(alert));
          playAlertSound();
        }
      } catch (_) {}
    };

    // Poll immediately then every 3 seconds
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [onNewOrder]);
}

/* ── Alert Banner ────────────────────────────────────────────────────────────*/
function OrderAlertBanner({ alerts, onDismiss, onViewOrders }) {
  if (alerts.length === 0) return null;
  const latest = alerts[alerts.length - 1];
  return (
    <div style={{
      position: 'fixed', top: 0, left: 200, right: 0, zIndex: 99999,
      background: 'linear-gradient(90deg,#dc2626,#b91c1c)',
      color: '#fff', padding: '0 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontFamily: "'Segoe UI', Arial, sans-serif",
      boxShadow: '0 4px 24px rgba(220,38,38,.5)',
      animation: 'nbAlertSlide 0.25s ease',
    }}>
      <style>{`
        @keyframes nbAlertSlide { from{transform:translateY(-100%)} to{transform:translateY(0)} }
        @keyframes nbAlertPulse { 0%,100%{opacity:1} 50%{opacity:.6} }
        .nb-alert-pulse { animation: nbAlertPulse 1s ease infinite }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '13px 0' }}>
        <span className="nb-alert-pulse" style={{ fontSize: 26 }}>🚨</span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.01em' }}>
            NEW SQ-OFF REQUEST
            <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 600, background: 'rgba(255,255,255,.2)', borderRadius: 5, padding: '2px 10px' }}>
              {latest.symbol}
            </span>
            <span style={{ marginLeft: 6, fontSize: 12, opacity: .85 }}>
              {latest.exchange} · {latest.segment}
            </span>
          </div>
          <div style={{ fontSize: 12, opacity: .85, marginTop: 3, display: 'flex', gap: 14 }}>
            <span>UCC: <strong>{latest.ucc}</strong></span>
            <span style={{ color: latest.side === 'SELL' ? '#fca5a5' : '#86efac' }}>
              {latest.side} {latest.quantity}
            </span>
            <span>{new Date(latest.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
            {alerts.length > 1 && (
              <span style={{ background: 'rgba(255,255,255,.25)', borderRadius: 99, padding: '1px 9px', fontSize: 11 }}>
                +{alerts.length - 1} more pending
              </span>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, padding: '13px 0', flexShrink: 0 }}>
        <button onClick={onViewOrders}
          style={{ background: '#fff', color: '#dc2626', border: 'none', borderRadius: 7, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          ⚡ Process Order
        </button>
        <button onClick={onDismiss}
          style={{ background: 'transparent', color: 'rgba(255,255,255,.8)', border: '1px solid rgba(255,255,255,.35)', borderRadius: 7, padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

/* ── Sidebar ─────────────────────────────────────────────────────────────────*/
const SEGMENTS = {
  NSE: [{ segment: 'CM', name: 'Cash Market', code: 'NSE · CM' }, { segment: 'FO', name: 'Futures & Options', code: 'NSE · FO' }],
  BSE: [{ segment: 'CM', name: 'Cash Market', code: 'BSE · CM' }, { segment: 'FO', name: 'Futures & Options', code: 'BSE · FO' }],
  MCX: [{ segment: 'FO', name: 'Futures & Options', code: 'MCX · FO' }],
};
const EXCHANGE_BADGE = {
  NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
};
const NAV_ITEMS = [
  { label: 'Segment Control', key: 'segments', section: 'Control'    },
  { label: 'Sq-Off Orders',   key: 'orders',   section: 'Control'    },
  { label: 'Client Alerts',   key: 'alerts',   section: 'Control'    },
  { label: 'B/F Upload',      key: 'bfupload', section: 'Operations' },
  { label: 'Sync Monitor',    key: 'syncmon',  section: 'Operations' },
  { label: 'Order Reports',   key: 'reports',  section: 'Reports'    },
  { label: 'Dealer Access',   key: 'dealer',   section: 'Reports'    },
  { label: 'Audit Logs',      key: 'logs',     section: 'Reports'    },
  { label: 'System Logs',     key: 'syslogs',  section: 'Reports'    },
  { label: 'User Management', key: 'users',    section: 'Admin'      },
  { label: 'Client Upload',   key: 'clients',  section: 'Admin'      },
];

function Sidebar({ activeNav, onNav, admin, onLogout, pendingCount }) {
  const sections = ['Control', 'Operations', 'Reports', 'Admin'];
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: 200, background: '#0f2744', display: 'flex', flexDirection: 'column', fontFamily: "'Segoe UI', Arial, sans-serif", zIndex: 100 }}>
      <div style={{ padding: '18px 16px', borderBottom: '0.5px solid rgba(255,255,255,0.1)' }}>
        <div style={{ background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, display: 'inline-block', letterSpacing: 0.5 }}>NAVIA BACKUP</div>
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginTop: 6 }}>RMS Admin Portal</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
        {sections.map(section => (
          <div key={section}>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, fontWeight: 600, letterSpacing: 1, padding: '10px 16px 4px', textTransform: 'uppercase' }}>{section}</div>
            {NAV_ITEMS.filter(n => n.section === section).map(item => (
              <div key={item.key} onClick={() => onNav(item.key)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', cursor: 'pointer', color: activeNav === item.key ? '#fff' : 'rgba(255,255,255,0.5)', background: activeNav === item.key ? 'rgba(37,99,235,0.25)' : 'transparent', borderLeft: `2px solid ${activeNav === item.key ? '#2563eb' : 'transparent'}`, fontSize: 12, transition: 'all 0.15s' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: activeNav === item.key ? '#2563eb' : 'rgba(255,255,255,0.2)' }} />
                {item.label}
                {/* Show pending order count badge on Sq-Off Orders */}
                {item.key === 'orders' && pendingCount > 0 && (
                  <span style={{ marginLeft: 'auto', background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 99, animation: 'nbAlertPulse 1s ease infinite' }}>
                    {pendingCount}
                  </span>
                )}
                {item.key === 'bfupload' && (
                  <span style={{ marginLeft: 'auto', background: 'rgba(251,191,36,0.2)', color: '#fbbf24', fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 3, letterSpacing: 0.3 }}>RMS</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div style={{ padding: '12px 16px', borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 2 }}>{admin.name}</div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginBottom: 10 }}>{admin.role}</div>
        <button onClick={onLogout} style={{ width: '100%', padding: '6px', borderRadius: 6, border: '0.5px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.4)', fontSize: 11, cursor: 'pointer' }}>Sign Out</button>
      </div>
    </div>
  );
}

function Topbar({ title, subtitle, admin }) {
  return (
    <div style={{ background: '#fff', borderBottom: '0.5px solid #e2e8f0', padding: '0 24px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{subtitle}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '0.5px solid #bfdbfe', padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{admin.role}</span>
        <span style={{ fontSize: 12, color: '#64748b' }}>{admin.name}</span>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{admin.name[0]}</div>
      </div>
    </div>
  );
}

function StatCards({ segments }) {
  const enabled = segments.filter(s => s.is_enabled).length;
  const cards = [
    { label: 'Total Segments', value: segments.length,           sub: 'NSE · BSE · MCX', color: '#1d4ed8' },
    { label: 'Active',         value: enabled,                   sub: 'Sq-off enabled',  color: '#15803d' },
    { label: 'Inactive',       value: segments.length - enabled, sub: 'Sq-off disabled', color: '#dc2626' },
    { label: "Today's Orders", value: 0,                         sub: 'Sq-off placed',   color: '#92400e' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 8, padding: '14px 16px' }}>
          <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: c.color }}>{c.value}</div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function SegmentPanel({ segments, onToggle, onAlert, toggling, communicating }) {
  const [activeEx, setActiveEx] = useState('NSE');
  const exchanges = ['NSE', 'BSE', 'MCX'];
  const getSegmentData = (exchange, seg) => segments.find(s => s.exchange === exchange && s.segment === seg.segment);
  return (
    <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Exchange Segment Control</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Enable a segment to allow sq-off · Alert clients when platform is down</div>
        </div>
        <button onClick={() => window.location.reload()} style={{ background: 'transparent', border: '0.5px solid #e2e8f0', color: '#64748b', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>↻ Refresh</button>
      </div>
      <div style={{ display: 'flex', borderBottom: '0.5px solid #e2e8f0' }}>
        {exchanges.map(ex => {
          const badge = EXCHANGE_BADGE[ex];
          const exSegs = segments.filter(s => s.exchange === ex);
          const activeCount = exSegs.filter(s => s.is_enabled).length;
          return (
            <div key={ex} onClick={() => setActiveEx(ex)} style={{ padding: '10px 28px', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: activeEx === ex ? '#1d4ed8' : '#64748b', borderBottom: `2px solid ${activeEx === ex ? '#2563eb' : 'transparent'}`, background: activeEx === ex ? '#f8faff' : 'transparent', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s' }}>
              <span style={{ background: badge.bg, color: badge.color, border: `0.5px solid ${badge.border}`, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{ex}</span>
              {activeCount > 0 && <span style={{ background: '#f0fdf4', color: '#15803d', border: '0.5px solid #bbf7d0', padding: '1px 6px', borderRadius: 10, fontSize: 9, fontWeight: 600 }}>{activeCount} active</span>}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 120px 140px 160px 220px', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' }}>
        {['Segment', 'Type', 'Status', 'Last Updated', 'Actions'].map(h => (
          <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
        ))}
      </div>
      {SEGMENTS[activeEx].map((seg, i) => {
        const data = getSegmentData(activeEx, seg) || {};
        const key  = `${activeEx}-${seg.segment}`;
        const isEnabled = data.is_enabled;
        const updatedAt = data.enabled_at ? new Date(data.enabled_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
        return (
          <div key={key} style={{ display: 'grid', gridTemplateColumns: '2fr 120px 140px 160px 220px', padding: '14px 20px', alignItems: 'center', borderBottom: i < SEGMENTS[activeEx].length - 1 ? '0.5px solid #f1f5f9' : 'none', background: isEnabled ? '#fafffe' : '#fff' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{seg.name}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{seg.code}</div>
            </div>
            <div><span style={{ background: EXCHANGE_BADGE[activeEx].bg, color: EXCHANGE_BADGE[activeEx].color, border: `0.5px solid ${EXCHANGE_BADGE[activeEx].border}`, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{seg.segment}</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: isEnabled ? '#16a34a' : '#d1d5db' }} />
              <span style={{ fontSize: 12, fontWeight: 500, color: isEnabled ? '#15803d' : '#9ca3af' }}>{isEnabled ? 'Active' : 'Inactive'}</span>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{updatedAt}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => onAlert(activeEx, seg.segment)} disabled={communicating[key]}
                style={{ background: 'transparent', border: '0.5px solid #e2e8f0', color: '#374151', padding: '5px 12px', borderRadius: 5, fontSize: 11, cursor: communicating[key] ? 'not-allowed' : 'pointer', opacity: communicating[key] ? 0.6 : 1 }}>
                {communicating[key] ? 'Sending...' : '📢 Alert Clients'}
              </button>
              <button onClick={() => onToggle(activeEx, seg.segment, isEnabled)} disabled={toggling[key]}
                style={{ padding: '5px 16px', borderRadius: 5, border: `0.5px solid ${isEnabled ? '#fecaca' : '#bbf7d0'}`, background: isEnabled ? '#fff5f5' : '#f0fdf4', color: isEnabled ? '#dc2626' : '#15803d', fontSize: 11, fontWeight: 500, cursor: toggling[key] ? 'not-allowed' : 'pointer', opacity: toggling[key] ? 0.6 : 1 }}>
                {toggling[key] ? '...' : isEnabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN AdminDashboard
   ═══════════════════════════════════════════════════════════════════════════*/
export default function AdminDashboard({ admin, onLogout }) {
  const [activeNav,     setActiveNav]     = useState('segments');
  const [segments,      setSegments]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [toggling,      setToggling]      = useState({});
  const [communicating, setCommunicating] = useState({});
  const [toast,         setToast]         = useState(null);

  // ── Real-time order alerts state ─────────────────────────────────────────
  const [orderAlerts,   setOrderAlerts]   = useState([]);  // pending new orders

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  // ── Handle incoming SSE order alert ──────────────────────────────────────
  const handleNewOrder = useCallback((orderData) => {
    setOrderAlerts(prev => [...prev, orderData]);
    showToast(`New sq-off: ${orderData.symbol} · ${orderData.ucc}`, 'alert');
  }, []);

  // ── Connect to SSE stream ─────────────────────────────────────────────────
  useOrderNotifications(handleNewOrder);

  const handleDismissAlerts = () => {
    const ids = orderAlerts.map(a => a.orderId);
    setOrderAlerts([]);
    // Mark as seen on backend so they don't reappear on next poll
    const token = localStorage.getItem('nb_admin_token');
    if (token && ids.length > 0) {
      fetch('/api/admin/notifications/seen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order_ids: ids }),
      }).catch(() => {});
    }
  };

  const handleViewOrders = () => {
    setActiveNav('orders');
    handleDismissAlerts();
  };

  const loadSegments = async () => {
    try { const res = await getSegments(); setSegments(res.data.segments); }
    catch { showToast('Failed to load segments.', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSegments(); }, []);

  const handleToggle = async (exchange, segment, currentlyEnabled) => {
    const key = `${exchange}-${segment}`;
    setToggling(t => ({ ...t, [key]: true }));
    try {
      await toggleSegment(exchange, segment, !currentlyEnabled, '');
      showToast(`${exchange} ${segment} ${!currentlyEnabled ? 'enabled' : 'disabled'} successfully`);
      loadSegments();
    } catch (err) { showToast(err.response?.data?.error || 'Failed to update.', 'error'); }
    finally { setToggling(t => ({ ...t, [key]: false })); }
  };

  const handleAlert = async (exchange, segment) => {
    const key = `${exchange}-${segment}`;
    if (!window.confirm(`Send Email + WhatsApp to all clients with open ${exchange} ${segment} positions?`)) return;
    setCommunicating(c => ({ ...c, [key]: true }));
    try {
      const res = await sendCommunication({ exchange, segment, send_email: true, send_whatsapp: true });
      showToast(`Sent to ${res.data.total_clients} clients — ${res.data.emails_sent} emails, ${res.data.whatsapp_sent} WhatsApp`);
    } catch (err) { showToast(err.response?.data?.error || 'Communication failed.', 'error'); }
    finally { setCommunicating(c => ({ ...c, [key]: false })); }
  };

  const navTitles = {
    segments: { title: 'Segment Control Panel',     sub: 'Manage exchange segments and client communications' },
    orders:   { title: 'Square-Off Orders',          sub: 'View and manage client sq-off requests'            },
    alerts:   { title: 'Client Alerts',              sub: 'Bulk communication history'                        },
    bfupload: { title: 'B/F Position File Upload',   sub: 'Upload daily NSE FO and MCX carried-forward files' },
    reports:  { title: 'Order Reports',              sub: 'Download exchange-wise reports'                     },
    dealer:   { title: 'Dealer Access',              sub: 'SSO and dealer order management'                    },
    logs:     { title: 'Audit Logs',                 sub: 'System activity and admin actions'                  },
    syslogs:  { title: 'System Logs',                sub: 'Complete audit trail for compliance and debugging'  },
    users:    { title: 'User Management',            sub: 'Admin and sub-user access control'                  },
    clients:  { title: 'Client Upload',              sub: 'Bulk upload clients via CSV file'                   },
    syncmon:  { title: 'Sync Monitor',               sub: 'DropCopy sync status and logs'                      },
  };
  const current = navTitles[activeNav] || navTitles.segments;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* ── Real-time order alert banner ── */}
      <OrderAlertBanner
        alerts={orderAlerts}
        onDismiss={handleDismissAlerts}
        onViewOrders={handleViewOrders}
      />

      {/* ── Toast ── */}
      {toast && toast.type !== 'alert' && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4', border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: toast.type === 'error' ? '#dc2626' : '#15803d', padding: '10px 18px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', maxWidth: 380 }}>
          {toast.msg}
        </div>
      )}

      {/* ── Sidebar with pending count badge ── */}
      <Sidebar
        activeNav={activeNav}
        onNav={setActiveNav}
        admin={admin}
        pendingCount={orderAlerts.length}
        onLogout={() => {
          localStorage.removeItem('nb_admin_token');
          localStorage.removeItem('nb_admin');
          onLogout();
        }}
      />

      {/* ── Main content ── */}
      <div style={{ marginLeft: 200, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Topbar title={current.title} subtitle={current.sub} admin={admin} />
        <div style={{ padding: '20px 24px', flex: 1 }}>
          {activeNav === 'segments' && (
            <>
              <div style={{ background: '#fffbeb', border: '0.5px solid #fcd34d', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b' }} />
                <span style={{ fontSize: 12, color: '#92400e' }}>
                  Platform monitoring active · {segments.filter(s => s.is_enabled).length} of {segments.length} segments enabled
                </span>
              </div>
              {loading
                ? <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 }}>Loading segments...</div>
                : <><StatCards segments={segments} /><SegmentPanel segments={segments} onToggle={handleToggle} onAlert={handleAlert} toggling={toggling} communicating={communicating} /></>
              }
            </>
          )}
          {activeNav === 'orders'   && <SqOffOrders />}
          {activeNav === 'alerts'   && <ClientAlerts />}
          {activeNav === 'bfupload' && <BFUpload />}
          {activeNav === 'reports'  && <OrderReports />}
          {activeNav === 'dealer'   && <DealerAccessPanel />}
          {activeNav === 'logs'     && <AuditLogs />}
          {activeNav === 'syslogs'  && <SystemLogsPanel />}
          {activeNav === 'syncmon'  && <SyncMonitor />}
          {activeNav === 'users'    && <UserManagement />}
          {activeNav === 'clients'  && <ClientUpload />}
        </div>
        <div style={{ textAlign: 'center', padding: '12px', fontSize: 10, color: '#cbd5e1', borderTop: '0.5px solid #e2e8f0' }}>
          Navia Markets Ltd · RMS Admin Terminal · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}